import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from "firebase/auth";
import { getDoc, doc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiEye, FiEyeOff } from "react-icons/fi";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [toastShown, setToastShown] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (error && !toastShown) {
      toast.error(error, { position: "top-right", autoClose: 3000 });
      setToastShown(true);
    }
    if (resetMessage && !toastShown) {
      toast.info(resetMessage, { position: "top-right", autoClose: 3000 });
      setToastShown(true);
    }
  }, [error, resetMessage, toastShown]);

  const fetchUserData = async (userId) => {
    try {
      console.log("📥 Fetching user data from Firestore...");
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log("✅ User data fetched:", userData);
        localStorage.setItem("users", JSON.stringify(userData));
      } else {
        console.error("❌ User data not found in Firestore.");
      }
    } catch (error) {
      console.error("🔥 Error fetching user data:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    setToastShown(false);
    setLoading(true);
  
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      // 🚨 Check verification
      if (!user.emailVerified) {
        await auth.signOut();
        setError("Please verify your email before logging in. Check your inbox.");
        setShowResendVerification(true);
        toast.error("⚠️ Email not verified. Please verify before logging in.");
        return;
      }
  
      // ✅ Fetch Firestore user data
      await fetchUserData(user.uid);
  
      toast.success("🎉 Welcome back!", { position: "top-right", autoClose: 3000 });
      setTimeout(() => navigate("/myassets"), 1500);
  
    } catch (err) {
      console.error("Login error:", err);
  
      let message = "Failed to log in. Please try again.";
      switch (err.code) {
        case "auth/user-not-found":
          message = "No account found with this email. Please sign up.";
          break;
        case "auth/wrong-password":
          message = "Incorrect password. Please try again.";
          break;
        case "auth/invalid-email":
          message = "Invalid email format.";
          break;
        case "auth/network-request-failed":
          message = "Network error. Please check your internet connection.";
          break;
        default:
          message = "Login failed. Please try again.";
      }
  
      setError(message);
      toast.error(message, { position: "top-right", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email to reset the password.");
      return;
    }
  
    try {
      await sendPasswordResetEmail(auth, email);
      toast.info("✅ Password reset email sent! Check your inbox.", {
        position: "top-right",
        autoClose: 3000,
      });
      setError("");
      setResetMessage("");
    } catch (err) {
      console.error("Forgot password error:", err);
  
      let message = "Failed to send reset email. Please try again.";
      if (err.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (err.code === "auth/invalid-email") {
        message = "Invalid email format.";
      } else if (err.code === "auth/network-request-failed") {
        message = "Network error. Please check your connection.";
      }
  
      setError(message);
      toast.error(message);
    }
  };
  


  const handleResendVerification = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      await sendEmailVerification(user);
      toast.success("📧 Verification email resent! Please check your inbox.");
      setResetMessage("Verification email sent! Please check your inbox.");
      setError("");
      setShowResendVerification(false);
  
      await auth.signOut(); // Sign out to prevent login until verified
    } catch (err) {
      console.error("Resend verification error:", err);
  
      let message = "Failed to send verification email. Please try again.";
      if (err.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (err.code === "auth/wrong-password") {
        message = "Incorrect password. Try again.";
      } else if (err.code === "auth/invalid-email") {
        message = "Invalid email format.";
      } else if (err.code === "auth/too-many-requests") {
        message = "Too many attempts. Please wait before trying again.";
      }
  
      setError(message);
      toast.error(message);
    }
  };
  

  return (
    // <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-purple-500 to-indigo-600 p-5">
      <div className="relative flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-[#f7faff] to-[#e8f0ff] p-5">

      <div className="absolute top-5 left-5">
       <a href="https://styleuai.com/">
      <img
        src="/logo.png"
        alt="StyleAI Logo"
        className="h-7 w-auto drop-shadow-md hover:scale-105 transition-transform duration-300"
      />
    </a>
    </div>
      <ToastContainer />

      <div className="w-full max-w-md bg-white/10 backdrop-blur-lg p-8 rounded-2xl shadow-2xl border border-white/20">
        {/* <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Welcome Back!</h2> */}
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Welcome!</h2>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-gray-800 mb-1 text-sm">Email</label>
            <input
              type="email"
              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white/90"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-gray-800 mb-1 text-sm">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white/90"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <button
  type="submit"
  className="w-full bg-indigo-500 text-white py-3 rounded-lg text-lg font-semibold transition-all duration-300 hover:bg-indigo-600 active:scale-95 disabled:opacity-50"
  disabled={loading}
>
  {loading ? "Logging in..." : "Login"}
</button>

        </form>

        {/* Forgot Password and Resend Verification */}
        <div className="flex flex-col items-center space-y-2 mt-4">
         <button
  onClick={handleForgotPassword}
  className={`text-gray-600 hover:text-indigo-600 hover:underline text-sm ${
    !email ? "opacity-50 cursor-not-allowed" : ""
  }`}
  disabled={!email}
>
  Forgot Password?
</button>

          {showResendVerification && (
            <button
              onClick={handleResendVerification}
              className="text-gray-600 hover:text-indigo-600 hover:underline text-sm"
            >
              Resend Verification Email
            </button>
          )}
        </div>

        <p className="text-center text-gray-800 mt-4">
          Don't have an account?{" "}
          <Link to="/signup-option" className="text-indigo-600 hover:text-indigo-800 font-medium
 hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
